/* SPDX-License-Identifier: Apache-2.0 */
/* SPDX-FileCopyrightText: 2021-2023 OKTET Labs Ltd. */
import { FC, useCallback, useMemo, useRef, useState } from 'react';

import { analyticsEventNames, trackEvent } from '@/bublik/features/analytics';
import { useGetTreeByRunIdQuery } from '@/services/bublik-api';
import { CardHeader, Skeleton, TooltipProvider } from '@/shared/tailwind-ui';
import { BublikErrorState } from '@/bublik/features/ui-state';

import { getTreeOnlyWithErrors } from './utils';

import { TreeView } from './tree-view';
import { TreeHeader } from '../../components';
import { useLogPage } from '../../hooks';

export const TreeLoading = () => {
	return (
		<div className="flex flex-col flex-grow bg-white rounded-md">
			<CardHeader label="Tree">
				<div className="flex items-stretch h-full gap-2">
					<Skeleton className="w-[26px] h-full rounded-md max-w-[217px]" />
					<Skeleton className="w-[57px] h-full rounded-md max-w-[217px]" />
					<Skeleton className="w-[120px] h-full rounded-md max-w-[217px]" />
				</div>
			</CardHeader>
			<div className="flex flex-col w-full h-full gap-1 px-4 pt-1 overflow-hidden">
				{Array.from({ length: 45 }).map((_, idx) => (
					<Skeleton
						key={idx}
						className="flex-shrink-0 w-full h-[22px] rounded"
					/>
				))}
			</div>
		</div>
	);
};

export interface TreeErrorProps {
	error: unknown;
}

export const TreeError = ({ error = { status: 400 } }: TreeErrorProps) => {
	return <BublikErrorState error={error} iconSize={36} className="h-full" />;
};

export interface TreeContainerProps {
	runId: string;
}

export const TreeContainer: FC<TreeContainerProps> = ({ runId }) => {
	const { focusId, isShowingRunLog, showRunLog } = useLogPage();
	const { data, isLoading, error } = useGetTreeByRunIdQuery(runId);
	const [showOnlyErrors, setShowOnlyErrors] = useState(false);

	const treeWithOnlyErrors = useMemo(() => {
		if (!data) return null;

		const treeOnlyWithErrors = getTreeOnlyWithErrors(data);

		if (!treeOnlyWithErrors) return null;

		return { ...data, tree: treeOnlyWithErrors };
	}, [data]);

	const scrollToFocusRef = useRef<(() => void) | undefined>();

	const handleScrollToFocusClick = useCallback(() => {
		trackEvent(analyticsEventNames.logTreeScrollToFocus, {
			hasFocusId: Boolean(focusId)
		});

		scrollToFocusRef.current?.();
	}, [focusId]);

	const handleNokClick = useCallback(() => {
		setShowOnlyErrors((prev) => {
			const next = !prev;

			trackEvent(analyticsEventNames.logTreeNokToggle, {
				enabled: next,
				hasErrors: treeWithOnlyErrors !== null
			});

			return next;
		});
	}, [treeWithOnlyErrors]);

	const handleRunButtonClick = useCallback(() => {
		trackEvent(analyticsEventNames.logTreeRunLogClick, {
			hasFocusId: Boolean(focusId)
		});

		showRunLog();
	}, [focusId, showRunLog]);

	if (isLoading) return <TreeLoading />;

	if (error) {
		return (
			<div className="flex flex-col flex-grow bg-white rounded-md">
				<TreeHeader
					hasErrors={treeWithOnlyErrors !== null}
					isNokMode={showOnlyErrors}
					isShowingRunLog={isShowingRunLog}
					onScrollToFocusClick={handleScrollToFocusClick}
					onRunButtonClick={handleRunButtonClick}
					onNokClick={handleNokClick}
				/>
				<div className="w-full h-full">
					<TreeError error={error} />
				</div>
			</div>
		);
	}

	return (
		<div className="flex flex-col flex-grow bg-white rounded-md">
			<TreeHeader
				hasErrors={treeWithOnlyErrors !== null}
				isNokMode={showOnlyErrors}
				isShowingRunLog={isShowingRunLog}
				onScrollToFocusClick={handleScrollToFocusClick}
				onRunButtonClick={handleRunButtonClick}
				onNokClick={handleNokClick}
			/>

			<div className="w-full h-full">
				<TooltipProvider disableHoverableContent>
					{data && !showOnlyErrors && (
						<TreeView
							data={data}
							itemSize={28}
							focusId={focusId?.toString() || data.main_package}
							ref={scrollToFocusRef}
						/>
					)}
					{data && treeWithOnlyErrors && showOnlyErrors && (
						<TreeView
							data={treeWithOnlyErrors}
							itemSize={28}
							focusId={focusId?.toString() || data.main_package}
							ref={scrollToFocusRef}
						/>
					)}
				</TooltipProvider>
			</div>
		</div>
	);
};
